import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Switch,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Autocomplete,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import HistoryIcon from '@mui/icons-material/History';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { scheduledJobsAPI } from '../services/api';
import { usePermissions } from '../context/PermissionsContext';
import { parseCron, QUARTER, HALF } from '../utils/cron';

function ScheduledJobs() {
  const { canEdit } = usePermissions();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [executing, setExecuting] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState({
    job_name: '',
    job_type: 'email',
    schedule_cron: '',
    config: {},
    is_active: true,
  });
  const [useGenerator, setUseGenerator] = useState(true);
  const [cronConfig, setCronConfig] = useState({
    frequency: 'daily',
    hour: '9',
    minute: '0',
    dayOfWeek: '1',
    dayOfMonth: '1',
  });
  const [fromEmails, setFromEmails] = useState([]);

  useEffect(() => {
    loadJobs();
    loadFromEmails();
  }, []);

  const loadFromEmails = async () => {
    try {
      const response = await scheduledJobsAPI.getFromEmails();
      setFromEmails(response.data.from_emails || []);
    } catch (err) {
      console.error('Failed to load from emails:', err);
    }
  };

  // Only rebuild the expression when a generator setting changes, never just because the
  // switch was flipped, or a saved schedule would be overwritten by the generator's last values
  useEffect(() => {
    if (useGenerator) {
      generateCron();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cronConfig]);

  const handleGeneratorToggle = (checked) => {
    if (checked) {
      const parsed = parseCron(formData.schedule_cron);
      if (parsed) setCronConfig(parsed);
    }
    setUseGenerator(checked);
  };

  const loadJobs = async () => {
    try {
      setLoading(true);
      const response = await scheduledJobsAPI.list();
      setJobs(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load jobs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const generateCron = () => {
    const { frequency, hour, minute, dayOfWeek, dayOfMonth } = cronConfig;
    let cron = '';

    switch (frequency) {
      case 'daily':
        cron = `${minute} ${hour} * * *`;
        break;
      case 'weekly':
        cron = `${minute} ${hour} * * ${dayOfWeek}`;
        break;
      case 'monthly':
        cron = `${minute} ${hour} ${dayOfMonth} * *`;
        break;
      case 'quarterly':
        cron = `${minute} ${hour} ${dayOfMonth} ${QUARTER} *`;
        break;
      case 'semiannual':
        cron = `${minute} ${hour} ${dayOfMonth} ${HALF} *`;
        break;
      case 'annual':
        cron = `${minute} ${hour} ${dayOfMonth} ${cronConfig.month || '1'} *`;
        break;
      case 'hourly':
        cron = `${minute} * * * *`;
        break;
      case 'every15min':
        cron = `*/15 * * * *`;
        break;
      default:
        cron = `${minute} ${hour} * * *`;
    }

    setFormData((current) => ({ ...current, schedule_cron: cron }));
  };

  const handleOpenDialog = (job = null) => {
    if (job) {
      setEditingJob(job);
      setFormData({
        job_name: job.job_name,
        job_type: job.job_type,
        schedule_cron: job.schedule_cron || '',
        config: job.config || {},
        is_active: job.is_active,
      });
      // Show the saved schedule in the generator when it can make it, otherwise as typed
      const parsed = parseCron(job.schedule_cron);
      if (parsed) setCronConfig(parsed);
      setUseGenerator(Boolean(parsed));
    } else {
      setEditingJob(null);
      setFormData({
        job_name: '',
        job_type: 'email',
        schedule_cron: '0 9 * * *',
        config: {},
        is_active: true,
      });
      setUseGenerator(true);
      setCronConfig({
        frequency: 'daily',
        hour: '9',
        minute: '0',
        dayOfWeek: '1',
        dayOfMonth: '1',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingJob(null);
  };

  const handleSave = async () => {
    try {
      // Validate email configuration if job type requires it
      if (formData.job_type === 'email' || formData.job_type === 'custom') {
        if (!formData.config?.subject) {
          setError('Email subject is required');
          return;
        }
        if (!formData.config?.body_text) {
          setError('Email body is required');
          return;
        }
        // Auto-generate HTML from text if not provided
        if (!formData.config?.body_html) {
          formData.config.body_html = `<html><body><p>${formData.config.body_text.replace(/\n/g, '<br>')}</p></body></html>`;
        }
      }

      if (editingJob) {
        await scheduledJobsAPI.update(editingJob.id, formData);
      } else {
        await scheduledJobsAPI.create(formData);
      }
      handleCloseDialog();
      loadJobs();
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save job');
    }
  };

  const handleDelete = async (jobId) => {
    if (window.confirm('Are you sure you want to delete this scheduled job?')) {
      try {
        await scheduledJobsAPI.delete(jobId);
        loadJobs();
        setError(null);
      } catch (err) {
        setError('Failed to delete job');
      }
    }
  };

  const handleExecute = async (jobId) => {
    if (!window.confirm('Execute this job now?')) return;

    setExecuting({ ...executing, [jobId]: true });
    try {
      const response = await scheduledJobsAPI.execute(jobId);
      setError(null);
      alert(`✓ ${response.data.message}`);
      loadJobs();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to execute job');
    } finally {
      setExecuting({ ...executing, [jobId]: false });
    }
  };

  const handleToggleActive = async (jobId, currentStatus) => {
    try {
      await scheduledJobsAPI.update(jobId, { is_active: !currentStatus });
      loadJobs();
    } catch (err) {
      setError('Failed to update job');
    }
  };

  const formatCron = (cron) => {
    if (!cron) return 'Not scheduled';
    const patterns = {
      '0 0 * * *': 'Daily at midnight',
      '0 9 * * *': 'Daily at 9 AM',
      '0 9 1 * *': 'Monthly on 1st at 9 AM',
      '0 9 * * 1': 'Every Monday at 9 AM',
      '0 */4 * * *': 'Every 4 hours',
      '*/15 * * * *': 'Every 15 minutes',
    };
    return patterns[cron] || cron;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Box>
          <Typography variant="h4">Scheduled Jobs</Typography>
          <Typography variant="body2" color="text.secondary">
            Automated tasks running on a schedule
          </Typography>
        </Box>
        {canEdit() && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Job
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Job Name</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell><strong>Schedule</strong></TableCell>
              <TableCell><strong>Last Run</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Active</strong></TableCell>
              {canEdit() && <TableCell><strong>Actions</strong></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((job) => (
              <TableRow key={job.id}>
                <TableCell>{job.job_name}</TableCell>
                <TableCell>
                  <Chip label={job.job_type} size="small" />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{formatCron(job.schedule_cron)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {job.schedule_cron}
                  </Typography>
                </TableCell>
                <TableCell>
                  {job.last_run_at
                    ? new Date(job.last_run_at).toLocaleString()
                    : 'Never'}
                </TableCell>
                <TableCell>
                  {job.last_run_status === 'success' && (
                    <Chip label="Success" color="success" size="small" />
                  )}
                  {job.last_run_status === 'failed' && (
                    <Chip label="Failed" color="error" size="small" />
                  )}
                  {!job.last_run_status && <Chip label="Pending" size="small" />}
                </TableCell>
                <TableCell>
                  {job.is_active ? (
                    <Chip label="Active" color="success" size="small" />
                  ) : (
                    <Chip label="Inactive" size="small" />
                  )}
                </TableCell>
                {canEdit() && (
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleExecute(job.id)}
                      color="success"
                      disabled={executing[job.id]}
                      title="Execute now"
                    >
                      <PlayArrowIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(job)}
                      color="primary"
                      title="Edit job"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(job.id)}
                      color="error"
                      title="Delete job"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {jobs.length === 0 && (
              <TableRow>
                <TableCell colSpan={canEdit() ? 7 : 6} align="center">
                  No scheduled jobs found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingJob ? 'Edit Job' : 'Add Job'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Job Name"
              value={formData.job_name}
              onChange={(e) => setFormData({ ...formData, job_name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              select
              label="Job Type"
              value={formData.job_type}
              onChange={(e) => setFormData({ ...formData, job_type: e.target.value })}
              fullWidth
              required
            >
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="patch_notification">Patch Notification</MenuItem>
              <MenuItem value="monthly_report">Monthly Report</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>

            {/* Schedule Configuration */}
            <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2, mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">Schedule</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useGenerator}
                      onChange={(e) => handleGeneratorToggle(e.target.checked)}
                      size="small"
                    />
                  }
                  label={useGenerator ? "Generator" : "Manual"}
                />
              </Box>

              {useGenerator ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    select
                    label="Frequency"
                    value={cronConfig.frequency}
                    onChange={(e) => {
                      setCronConfig({ ...cronConfig, frequency: e.target.value });
                    }}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="every15min">Every 15 minutes</MenuItem>
                    <MenuItem value="hourly">Every hour</MenuItem>
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="quarterly">Quarterly (Jan, Apr, Jul, Oct)</MenuItem>
                    <MenuItem value="semiannual">Semiannual (Jan, Jul)</MenuItem>
                    <MenuItem value="annual">Annual</MenuItem>
                  </TextField>

                  {['daily', 'weekly', 'monthly', 'quarterly', 'semiannual', 'annual'].includes(cronConfig.frequency) && (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        select
                        label="Hour"
                        value={cronConfig.hour}
                        onChange={(e) => {
                          setCronConfig({ ...cronConfig, hour: e.target.value });
                        }}
                        fullWidth
                        size="small"
                      >
                        {Array.from({ length: 24 }, (_, i) => (
                          <MenuItem key={i} value={i.toString()}>
                            {i.toString().padStart(2, '0')}:00 ({i < 12 ? 'AM' : 'PM'})
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        select
                        label="Minute"
                        value={cronConfig.minute}
                        onChange={(e) => {
                          setCronConfig({ ...cronConfig, minute: e.target.value });
                        }}
                        fullWidth
                        size="small"
                      >
                        {[0, 15, 30, 45].map((min) => (
                          <MenuItem key={min} value={min.toString()}>
                            :{min.toString().padStart(2, '0')}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  )}

                  {cronConfig.frequency === 'hourly' && (
                    <TextField
                      select
                      label="Minute"
                      value={cronConfig.minute}
                      onChange={(e) => {
                        setCronConfig({ ...cronConfig, minute: e.target.value });
                      }}
                      fullWidth
                      size="small"
                    >
                      {[0, 15, 30, 45].map((min) => (
                        <MenuItem key={min} value={min.toString()}>
                          :{min.toString().padStart(2, '0')}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}

                  {cronConfig.frequency === 'weekly' && (
                    <TextField
                      select
                      label="Day of Week"
                      value={cronConfig.dayOfWeek}
                      onChange={(e) => {
                        setCronConfig({ ...cronConfig, dayOfWeek: e.target.value });
                      }}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="0">Sunday</MenuItem>
                      <MenuItem value="1">Monday</MenuItem>
                      <MenuItem value="2">Tuesday</MenuItem>
                      <MenuItem value="3">Wednesday</MenuItem>
                      <MenuItem value="4">Thursday</MenuItem>
                      <MenuItem value="5">Friday</MenuItem>
                      <MenuItem value="6">Saturday</MenuItem>
                    </TextField>
                  )}

                  {cronConfig.frequency === 'annual' && (
                    <TextField
                      select
                      label="Month"
                      value={cronConfig.month || '1'}
                      onChange={(e) => setCronConfig({ ...cronConfig, month: e.target.value })}
                      fullWidth
                      size="small"
                    >
                      {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((m, i) => (
                        <MenuItem key={m} value={String(i + 1)}>{m}</MenuItem>
                      ))}
                    </TextField>
                  )}

                  {['monthly', 'quarterly', 'semiannual', 'annual'].includes(cronConfig.frequency) && (
                    <TextField
                      select
                      label="Day of Month"
                      value={cronConfig.dayOfMonth}
                      onChange={(e) => {
                        setCronConfig({ ...cronConfig, dayOfMonth: e.target.value });
                      }}
                      fullWidth
                      size="small"
                    >
                      {Array.from({ length: 31 }, (_, i) => (
                        <MenuItem key={i + 1} value={(i + 1).toString()}>
                          {i + 1}{i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}

                  <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Generated Cron Expression:
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                      {formData.schedule_cron}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {formatCron(formData.schedule_cron)}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <TextField
                  label="Cron Expression"
                  value={formData.schedule_cron}
                  onChange={(e) => setFormData({ ...formData, schedule_cron: e.target.value })}
                  fullWidth
                  size="small"
                  helperText="Format: minute hour day month day-of-week (e.g., '0 9 * * *' for daily at 9am)"
                />
              )}
            </Box>

            {/* Email Configuration - Show for email/custom job types */}
            {(formData.job_type === 'email' || formData.job_type === 'custom') && (
              <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2, mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>Email Configuration</Typography>

                <Autocomplete
                  freeSolo
                  options={fromEmails}
                  value={formData.config?.from_email || 'noreply@starlimscloud.com'}
                  onChange={(e, newValue) => setFormData({
                    ...formData,
                    config: { ...formData.config, from_email: newValue || 'noreply@starlimscloud.com' }
                  })}
                  onInputChange={(e, newValue) => setFormData({
                    ...formData,
                    config: { ...formData.config, from_email: newValue || 'noreply@starlimscloud.com' }
                  })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="From Email Address"
                      size="small"
                      helperText="Select from previously used or enter a new email address (default: noreply@starlimscloud.com)"
                    />
                  )}
                  fullWidth
                  sx={{ mb: 2 }}
                />

                <TextField
                  select
                  label="Recipients"
                  value={formData.config?.recipient_type || 'all_customers'}
                  onChange={(e) => setFormData({
                    ...formData,
                    config: { ...formData.config, recipient_type: e.target.value }
                  })}
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                >
                  <MenuItem value="all_customers">All Active Customer Contacts</MenuItem>
                  <MenuItem value="specific_emails">Specific Email Addresses</MenuItem>
                </TextField>

                {formData.config?.recipient_type === 'specific_emails' && (
                  <TextField
                    label="Email Addresses (comma-separated)"
                    value={formData.config?.emails?.join(', ') || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      config: {
                        ...formData.config,
                        emails: e.target.value.split(',').map(email => email.trim()).filter(e => e)
                      }
                    })}
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    sx={{ mb: 2 }}
                    helperText="Enter email addresses separated by commas"
                  />
                )}

                <TextField
                  label="Email Subject"
                  value={formData.config?.subject || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    config: { ...formData.config, subject: e.target.value }
                  })}
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                  required
                  helperText="Filled in each time the job runs: {{month_year}} (November 2026), {{month}}, {{year}}, {{previous_month_year}}, {{next_month_year}}. Example: Monthly Patching Ticket - {{month_year}}"
                />

                <TextField
                  label="Email Body (Plain Text)"
                  value={formData.config?.body_text || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    config: { ...formData.config, body_text: e.target.value }
                  })}
                  fullWidth
                  multiline
                  rows={4}
                  size="small"
                  sx={{ mb: 2 }}
                  helperText="Plain text version of the email"
                />

                <TextField
                  label="Email Body (HTML - Optional)"
                  value={formData.config?.body_html || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    config: { ...formData.config, body_html: e.target.value }
                  })}
                  fullWidth
                  multiline
                  rows={6}
                  size="small"
                  helperText="HTML version for richer formatting. If empty, will use plain text."
                />
              </Box>
            )}

            {/* Patch Notification - Show info only */}
            {formData.job_type === 'patch_notification' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                This job will automatically send patch notifications to all active customers
                who have opted in for patch notifications. The email will include all approved
                patches from the current month.
              </Alert>
            )}

            {/* Monthly Report - Show info only */}
            {formData.job_type === 'monthly_report' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                This job will send monthly reports to customers. Configure the email content above.
              </Alert>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {editingJob ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ScheduledJobs;
